import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useSiteConfig, resolveUploadUrl } from '../utils/siteConfig';
import { ClientLogosCarousel } from '../components/ClientLogosCarousel';
import * as Icons from 'lucide-react';
import { AnimatedNumber } from '../components/AnimatedNumber';

/** Safely normalize an about field to {text, image1, image2} regardless of stored format */
function safeAboutData(raw: any): { text: string; image1: string; image2: string } {
  if (typeof raw === 'string') return { text: raw, image1: '', image2: '' };
  if (raw && typeof raw === 'object') {
    if (typeof raw.text === 'string') return { text: raw.text, image1: raw.image1 || '', image2: raw.image2 || '' };
    const chars: string[] = [];
    let i = 0;
    while (typeof raw[i] === 'string') { chars.push(raw[i]); i++; }
    return { text: chars.join(''), image1: raw.image1 || '', image2: raw.image2 || '' };
  }
  return { text: '', image1: '', image2: '' };
}

export function About() {
  const { config } = useSiteConfig();
  const [activeModal, setActiveModal] = useState<number | null>(null);
  const [imgHovered, setImgHovered] = useState(false);

  const statsIcons: Record<string, any> = {
    Briefcase: Icons.Briefcase,
    Heart: Icons.Heart,
    Users: Icons.Users
  };

  // Unified array of all 6 cards
  const allCards = [
    {
      icon: Icons.Sparkles,
      title: 'Who We Are',
      data: config.about.whoWeAre,
      colorFrom: 'from-indigo-500',
      colorTo: 'to-purple-500',
      delay: 0
    },
    {
      icon: Icons.Cpu,
      title: 'Our Expertise',
      data: config.about.expertise,
      colorFrom: 'from-emerald-500',
      colorTo: 'to-teal-500',
      delay: 0.1
    },
    {
      icon: Icons.Handshake,
      title: 'Our Partners',
      data: config.about.partners,
      colorFrom: 'from-amber-500',
      colorTo: 'to-orange-500',
      delay: 0.2
    },
    {
      icon: Icons.Compass,
      title: 'Our Approach',
      data: config.about.approach,
      colorFrom: 'from-blue-500',
      colorTo: 'to-indigo-500',
      delay: 0.3
    },
    {
      icon: Icons.Target,
      title: 'Our Mission',
      data: config.about.mission,
      colorFrom: 'from-rose-500',
      colorTo: 'to-pink-500',
      delay: 0
    },
    {
      icon: Icons.Eye,
      title: 'Our Vision',
      data: config.about.vision,
      colorFrom: 'from-violet-500',
      colorTo: 'to-fuchsia-500',
      delay: 0.15
    }
  ];

  const openModal = (index: number) => {
    setActiveModal(index);
    setImgHovered(false);
  };

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setImgHovered(false);
  }, []);

  const prevCard = useCallback(() => {
    setActiveModal(prev => prev !== null ? (prev - 1 + allCards.length) % allCards.length : null);
    setImgHovered(false);
  }, [allCards.length]);

  const nextCard = useCallback(() => {
    setActiveModal(prev => prev !== null ? (prev + 1) % allCards.length : null);
    setImgHovered(false);
  }, [allCards.length]);

  // Keyboard navigation
  useEffect(() => {
    if (activeModal === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') prevCard();
      if (e.key === 'ArrowRight') nextCard();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeModal, closeModal, prevCard, nextCard]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeModal !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [activeModal]);

  const modalCard = activeModal !== null ? allCards[activeModal] : null;
  const modalData = modalCard ? safeAboutData(modalCard.data) : null;

  return (
    <div className="pt-24 bg-slate-50 min-h-screen text-slate-900 overflow-x-hidden">

      {/* Hero Section */}
      <section className="relative py-16 md:py-28 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1920&q=80"
            alt="IT Professional Working"
            className="w-full h-full object-cover filter blur-[2px] opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-50/30 via-transparent to-slate-50" />
        </div>
        <motion.div
          className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-primary/10 to-primary/15 rounded-full blur-3xl"
          animate={{ scale: [1, 1.15, 1], x: [0, -30, 0], y: [0, 20, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 -left-40 w-96 h-96 bg-gradient-to-tr from-primary/10 to-primary/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], x: [0, 40, 0], y: [0, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="px-4 py-1.5 bg-primary/5 border border-primary/20 text-primary rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-6 shadow-sm">
              About BigGroww
            </span>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight text-slate-900 mb-8 leading-[1.15]">
              We Don't Just Grow Brands.<br />
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                We Build Their Future.
              </span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Client Logos Carousel */}
      <ClientLogosCarousel logos={config.home.hero.clientLogos} />

      {/* ── 4 Philosophy Cards ───────────────────────────────────────────────── */}
      <section className="py-12 px-4 md:px-8 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[100px] -z-10 pointer-events-none" />
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {allCards.slice(0, 4).map((card, i) => {
              const safeData = safeAboutData(card.data);
              return (
                <motion.div
                  key={i}
                  onClick={() => openModal(i)}
                  initial={{ opacity: 0, y: 50, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.7, delay: card.delay, type: 'spring', bounce: 0.3 }}
                  whileHover={{ y: -10 }}
                  className="group relative p-8 md:p-10 rounded-[2.5rem] bg-white/80 backdrop-blur-xl border border-white/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-500 flex flex-col gap-6 overflow-hidden cursor-pointer"
                >
                  {/* Hover glow */}
                  <div className={`absolute top-0 right-0 w-72 h-72 bg-gradient-to-bl ${card.colorFrom} ${card.colorTo} opacity-0 group-hover:opacity-[0.07] transition-all duration-700 rounded-full blur-3xl -z-10 translate-x-20 -translate-y-20 group-hover:translate-x-0 group-hover:translate-y-0`} />

                  {/* Click hint */}
                  <div className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/70 backdrop-blur-sm border border-slate-200/60 flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Icons.Expand size={13} className="text-slate-500" />
                  </div>

                  <h3 className={`text-3xl font-black tracking-tight relative z-10 bg-gradient-to-r ${card.colorFrom} ${card.colorTo} bg-clip-text text-transparent`}>
                    {card.title}
                  </h3>

                  {(safeData.image1 || safeData.image2) && (
                    <div className="relative w-full h-48 rounded-2xl overflow-hidden shadow-sm border border-slate-100/50">
                      {safeData.image1 && (
                        <img src={resolveUploadUrl(safeData.image1)} alt={card.title} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-0" />
                      )}
                      {safeData.image2 && (
                        <img src={resolveUploadUrl(safeData.image2)} alt={`${card.title} hover`} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${safeData.image1 ? 'opacity-0 group-hover:opacity-100' : ''}`} />
                      )}
                    </div>
                  )}

                  <p className="text-slate-600 leading-relaxed text-base relative z-10 group-hover:text-slate-800 transition-colors duration-300 line-clamp-4">
                    {safeData.text}
                  </p>

                  {/* Expand CTA */}
                  <div className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r ${card.colorFrom} ${card.colorTo} bg-clip-text text-transparent`}>
                    Read More <Icons.ArrowRight size={12} />
                  </div>

                  {/* Bottom border animation */}
                  <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1.5 w-0 bg-gradient-to-r ${card.colorFrom} ${card.colorTo} group-hover:w-full transition-all duration-500 ease-out rounded-t-full opacity-0 group-hover:opacity-100`} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Mission & Vision (cards 4 & 5) ──────────────────────────────────── */}
      <section className="pb-12 md:pb-24 px-4 md:px-8 relative">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 lg:gap-12 relative z-10">
          {allCards.slice(4).map((card, i) => {
            const safeData = safeAboutData(card.data);
            const globalIdx = 4 + i;
            return (
              <motion.div
                key={i}
                onClick={() => openModal(globalIdx)}
                className="group relative p-8 md:p-12 rounded-3xl bg-white/80 backdrop-blur-xl border border-white/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] flex flex-col justify-between overflow-hidden cursor-pointer transition-all duration-500"
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.7, delay: card.delay, type: 'spring', bounce: 0.3 }}
                whileHover={{ y: -10 }}
              >
                {/* Hover glow */}
                <div className={`absolute top-0 right-0 w-80 h-80 bg-gradient-to-bl ${card.colorFrom} ${card.colorTo} opacity-0 group-hover:opacity-[0.07] transition-all duration-700 rounded-full blur-3xl -z-10 translate-x-20 -translate-y-20 group-hover:translate-x-0 group-hover:translate-y-0`} />

                {/* Click hint */}
                <div className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/70 backdrop-blur-sm border border-slate-200/60 flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Icons.Expand size={13} className="text-slate-500" />
                </div>

                <div>
                  <h2 className={`text-4xl font-black tracking-tight mb-6 relative z-10 bg-gradient-to-r ${card.colorFrom} ${card.colorTo} bg-clip-text text-transparent`}>
                    {card.title}
                  </h2>

                  {(safeData.image1 || safeData.image2) && (
                    <div className="relative w-full h-56 rounded-2xl overflow-hidden mb-6 shadow-sm border border-slate-100/50">
                      {safeData.image1 && (
                        <img src={resolveUploadUrl(safeData.image1)} alt={card.title} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-0" />
                      )}
                      {safeData.image2 && (
                        <img src={resolveUploadUrl(safeData.image2)} alt={`${card.title} hover`} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${safeData.image1 ? 'opacity-0 group-hover:opacity-100' : ''}`} />
                      )}
                    </div>
                  )}

                  <p className="text-slate-600 text-lg leading-relaxed relative z-10 group-hover:text-slate-800 transition-colors duration-300 line-clamp-3">
                    {safeData.text}
                  </p>
                </div>

                {/* Expand CTA */}
                <div className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest mt-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r ${card.colorFrom} ${card.colorTo} bg-clip-text text-transparent`}>
                  Read More <Icons.ArrowRight size={12} />
                </div>

                <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1.5 w-0 bg-gradient-to-r ${card.colorFrom} ${card.colorTo} group-hover:w-full transition-all duration-500 ease-out rounded-t-full opacity-0 group-hover:opacity-100`} />
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ── FULLSCREEN MODAL ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {activeModal !== null && modalCard && modalData && (
          <motion.div
            key="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-slate-900/70 backdrop-blur-md"
            onClick={closeModal}
          >
            <motion.div
              key={activeModal}
              initial={{ opacity: 0, scale: 0.92, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 24 }}
              transition={{ type: 'spring', bounce: 0.28, duration: 0.45 }}
              onClick={e => e.stopPropagation()}
              className="relative w-full max-w-3xl max-h-[88vh] bg-white rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Gradient top accent bar */}
              <div className={`h-1.5 w-full bg-gradient-to-r ${modalCard.colorFrom} ${modalCard.colorTo} shrink-0`} />

              {/* Close button */}
              <button
                onClick={closeModal}
                className="absolute top-5 right-5 z-20 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-all hover:scale-110 active:scale-95 cursor-pointer"
                aria-label="Close"
              >
                <Icons.X size={16} />
              </button>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto p-8 md:p-12">
                {/* Header */}
                <div className="flex items-start gap-4 mb-8">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${modalCard.colorFrom} ${modalCard.colorTo} text-white flex items-center justify-center shrink-0 shadow-lg`}>
                    <modalCard.icon size={22} />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">
                      {activeModal + 1} of {allCards.length}
                    </p>
                    <h2 className={`text-3xl md:text-4xl font-black tracking-tight bg-gradient-to-r ${modalCard.colorFrom} ${modalCard.colorTo} bg-clip-text text-transparent leading-tight`}>
                      {modalCard.title}
                    </h2>
                  </div>
                </div>

                {/* Images */}
                {(modalData.image1 || modalData.image2) && (
                  <div
                    className="relative w-full h-56 md:h-72 rounded-2xl overflow-hidden mb-8 shadow-md border border-slate-100 cursor-pointer"
                    onMouseEnter={() => setImgHovered(true)}
                    onMouseLeave={() => setImgHovered(false)}
                  >
                    {modalData.image1 && (
                      <img
                        src={resolveUploadUrl(modalData.image1)}
                        alt={modalCard.title}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imgHovered && modalData.image2 ? 'opacity-0' : 'opacity-100'}`}
                      />
                    )}
                    {modalData.image2 && (
                      <img
                        src={resolveUploadUrl(modalData.image2)}
                        alt={`${modalCard.title} alt`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imgHovered || !modalData.image1 ? 'opacity-100' : 'opacity-0'}`}
                      />
                    )}
                    {/* Hover label */}
                    {modalData.image1 && modalData.image2 && (
                      <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/40 backdrop-blur-sm rounded-full text-white text-[10px] font-semibold flex items-center gap-1.5">
                        <Icons.MousePointerClick size={10} /> Hover to switch image
                      </div>
                    )}
                  </div>
                )}

                {/* Full text */}
                <p className="text-slate-700 text-base md:text-lg leading-relaxed font-sans">
                  {modalData.text}
                </p>
              </div>

              {/* ── Navigation footer ── */}
              <div className="shrink-0 border-t border-slate-100 px-8 py-5 flex items-center justify-between bg-white/80 backdrop-blur-sm">
                {/* Dot indicators */}
                <div className="flex items-center gap-2">
                  {allCards.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => openModal(idx)}
                      className={`rounded-full transition-all duration-300 cursor-pointer ${
                        idx === activeModal
                          ? `w-6 h-2.5 bg-gradient-to-r ${modalCard.colorFrom} ${modalCard.colorTo}`
                          : 'w-2 h-2 bg-slate-200 hover:bg-slate-300'
                      }`}
                      aria-label={`Go to ${allCards[idx].title}`}
                    />
                  ))}
                </div>

                {/* Prev / Next */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={prevCard}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-sm font-semibold transition-all hover:shadow-md active:scale-95 cursor-pointer"
                    aria-label="Previous"
                  >
                    <Icons.ChevronLeft size={16} /> Prev
                  </button>
                  <button
                    onClick={nextCard}
                    className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition-all hover:shadow-lg active:scale-95 cursor-pointer bg-gradient-to-r ${modalCard.colorFrom} ${modalCard.colorTo}`}
                    aria-label="Next"
                  >
                    Next <Icons.ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Core Values Section */}
      <section className="py-12 md:py-24 px-4 md:px-8 bg-slate-50 border-t border-slate-200">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-blue-600 text-sm font-bold uppercase tracking-wider mb-3 block">
              What Drives Us
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 heading-glow">
              Our Core Values
            </h2>
          </motion.div>

          <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 overflow-x-auto sm:overflow-visible snap-x snap-mandatory pb-8 sm:pb-0 no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 items-stretch">
            {config.about.coreValues.map((value, index) => {
              const IconComponent = (Icons as Record<string, any>)[value.icon] || Icons.Zap;
              return (
                <motion.div
                  key={index}
                  className="w-[85vw] sm:w-auto h-full shrink-0 snap-center p-8 rounded-3xl glass-card flex flex-col gap-6 shadow-sm border border-slate-200/50"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  whileHover={{ y: -6, scale: 1.02 }}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${value.color || 'from-primary to-primary'} text-white flex items-center justify-center shadow-md`}>
                    <IconComponent size={24} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-slate-900 mb-2">{value.title}</h4>
                    <p className="text-slate-500 leading-relaxed text-sm">{value.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Dashboard Counter */}
      <section className="py-10 md:py-20 px-4 md:px-8 max-w-6xl mx-auto">
        <div className="bg-primary rounded-[2.5rem] p-8 md:p-12 text-white relative overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-grid-white/[0.05] [mask-image:linear-gradient(0deg,white,transparent)]" />
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative z-10 text-center">
            {[
              { num: '600+', label: 'Projects Delivered', icon: 'Briefcase' },
              { num: '98%', label: 'Client Satisfaction', icon: 'Heart' },
              { num: '50+', label: 'Team Members', icon: 'Users' },
            ].map((item, index) => {
              const Icon = statsIcons[item.icon] || Icons.Globe;
              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="flex flex-col items-center gap-3"
                >
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white/80">
                    <Icon size={24} />
                  </div>
                  <div className="text-5xl font-black tracking-tight flex justify-center"><AnimatedNumber value={item.num} /></div>
                  <div className="text-white/90 text-sm font-semibold uppercase tracking-wider">{item.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Promise Banner */}
      <section className="py-12 md:py-24 px-4 md:px-8 relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col gap-8"
          >
            <span className="px-4 py-1.5 bg-primary/5 border border-primary/20 text-primary rounded-full text-xs font-semibold uppercase tracking-wider inline-block mx-auto shadow-sm">
              {config.about.promise.tagline}
            </span>
            <p className="text-slate-500 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-sans">
              Every strategy we develop, every design we create, and every solution we deliver is driven by one purpose: helping your business grow with confidence.
            </p>
            <div className="w-24 h-1 bg-primary mx-auto rounded-full my-6" />
            <h3 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-primary via-accent to-indigo-600 bg-clip-text text-transparent italic">
              "{config.about.promise.quote}"
            </h3>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
